import React from 'react';
import { Calendar, Flag, Users, Clock, ListChecks, History } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const statusConfig = {
  backlog: { label: 'Backlog', color: 'bg-slate-100 text-slate-700' },
  todo: { label: 'A Fazer', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'Em Progresso', color: 'bg-amber-100 text-amber-700' },
  review: { label: 'Em Revisão', color: 'bg-purple-100 text-purple-700' },
  done: { label: 'Concluído', color: 'bg-emerald-100 text-emerald-700' }
};

const priorityConfig = {
  low: { label: 'Baixa', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Média', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'Alta', color: 'bg-amber-100 text-amber-700' },
  urgent: { label: 'Urgente', color: 'bg-red-100 text-red-700' }
};

const avatarColors = {
  blue: 'from-blue-500 to-cyan-500',
  purple: 'from-purple-500 to-pink-500',
  green: 'from-emerald-500 to-teal-500',
  orange: 'from-orange-500 to-amber-500',
  red: 'from-red-500 to-rose-500',
  indigo: 'from-indigo-500 to-blue-500'
};

export default function TableView({ tasks, members, onTaskClick, onHistoryClick }) {
  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl border-2 border-slate-200 overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-slate-50/80 border-b border-slate-200">
            <th className="font-bold text-left px-4 py-3 text-sm">Tarefa</th>
            <th className="font-bold text-left px-4 py-3 text-sm">Status</th>
            <th className="font-bold text-left px-4 py-3 text-sm">Prioridade</th>
            <th className="font-bold text-left px-4 py-3 text-sm">Responsáveis</th>
            <th className="font-bold text-left px-4 py-3 text-sm">Data Limite</th>
            <th className="font-bold text-left px-4 py-3 text-sm">Progresso</th>
            <th className="font-bold text-left px-4 py-3 text-sm w-[80px]">Ações</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => {
            const status = statusConfig[task.status] || statusConfig.backlog;
            const priority = priorityConfig[task.priority] || priorityConfig.medium;
            const assignedMembers = members.filter(m => task.assignees?.includes(m.id));
            const subtasksTotal = task.subtasks?.length || 0;
            const subtasksCompleted = task.subtasks?.filter(st => st.completed).length || 0;
            const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done';

            return (
              <tr 
                key={task.id}
                className="border-b border-slate-200 cursor-pointer hover:bg-blue-50/50 transition-colors"
                onClick={() => onTaskClick(task)}
              >
                {/* Task Title */}
                <td className="font-medium max-w-[300px] px-4 py-3">
                  <div>
                    <div className="font-semibold text-slate-900 line-clamp-1">
                      {task.title}
                    </div>
                    {task.description && (
                      <div className="text-xs text-slate-500 line-clamp-1 mt-1">
                        {task.description}
                      </div>
                    )}
                    {task.tags && task.tags.length > 0 && (
                      <div className="flex gap-1 mt-1.5">
                        {task.tags.slice(0, 2).map((tag, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </td>

                {/* Status */}
                <td className="px-4 py-3">
                  <span className={`${status.color} font-semibold px-2 py-1 rounded text-xs inline-block`}>
                    {status.label}
                  </span>
                </td>

                {/* Priority */}
                <td className="px-4 py-3">
                  <span className={`${priority.color} font-semibold px-2 py-1 rounded text-xs inline-flex items-center`}>
                    <Flag className="w-3 h-3 mr-1" />
                    {priority.label}
                  </span>
                </td>

                {/* Assignees */}
                <td className="px-4 py-3">
                  {assignedMembers.length > 0 ? (
                    <div className="flex -space-x-2">
                      {assignedMembers.slice(0, 3).map((member, idx) => {
                        const colorGradient = avatarColors[member.avatar_color] || avatarColors.blue;
                        return (
                          <div 
                            key={member.id}
                            className={`w-7 h-7 rounded-full bg-gradient-to-br ${colorGradient} flex items-center justify-center shadow-md ring-2 ring-white`}
                            style={{ zIndex: 3 - idx }}
                            title={member.name}
                          >
                            <span className="text-xs font-bold text-white">
                              {member.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        );
                      })}
                      {assignedMembers.length > 3 && (
                        <div className="w-7 h-7 rounded-full bg-slate-400 flex items-center justify-center shadow-md ring-2 ring-white text-xs font-bold text-white">
                          +{assignedMembers.length - 3}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">-</span>
                  )}
                </td>

                {/* Due Date */}
                <td className="px-4 py-3">
                  {task.due_date ? (
                    <span className={`flex items-center text-xs font-medium px-2 py-1 rounded-lg w-fit ${
                      isOverdue ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      <Calendar className="w-3 h-3 mr-1" />
                      {format(new Date(task.due_date), 'dd/MM/yy', { locale: ptBR })}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">-</span>
                  )}
                </td>

                {/* Progress */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {subtasksTotal > 0 ? (
                      <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded inline-flex items-center">
                        <ListChecks className="w-3 h-3 mr-1" />
                        {subtasksCompleted}/{subtasksTotal}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                    {task.estimated_hours && (
                      <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded inline-flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {task.estimated_hours}h
                      </span>
                    )}
                  </div>
                </td>

                {/* Actions */}
                <td className="px-4 py-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onHistoryClick(task);
                    }}
                    className="h-7 w-7 flex items-center justify-center text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    <History className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}